import _ from 'lodash';

// LEN-213: one parser for every guideline-citation marker shape the AI emits, shared by
// the AI chat and AI Activity surfaces so both render the same citation cards / chips.
//
//   [^cite:afr-fha-203b-1-3]        cleaned, render-ready form (chat + cleaned step output)
//   [[CITE:48217]]                  a section's numeric CitationNumber (DEV-3607)
//   [[CITE:afr-fha-203b-1-3]]       a citation key copied from an earlier step's output
//   [[CITE:FNMA|B3-6-02|DTI]]       legacy three-part form
//
// Every raw form is normalised to the cleaned `[^cite:<key>]` form, keyed by the same
// citation key / number the API returns as `citationId`, so one lookup resolves them all.
const THREE_PART_MARKER_REGEX = /\[\[CITE:\s*([^|\]]+?)\s*\|\s*([^|\]]+?)\s*\|[^\]]*\]\]/gi;
const KEY_OR_REF_MARKER_REGEX = /\[\[CITE:\s*([a-z0-9][a-z0-9._-]*)\s*\]\]/gi;
const CLEAN_MARKER_SPLIT_REGEX = /(\[\^cite:[^\]\s]+\])/gi;
const CLEAN_MARKER_KEY_REGEX = /^\[\^cite:([^\]\s]+)\]$/i;
const ANY_MARKER_TEST_REGEX = /\[\[CITE:|\[\^cite:/i;
const NUMERIC_KEY_REGEX = /^\d{1,9}$/;

// Mirrors the API's citation-key slug (GuidelineCitationKeys.Slugify): lower-case, every
// run of space . _ - folded to one hyphen, other punctuation dropped.
const slugifyCitationPart = value =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 ._-]/g, '')
    .replace(/[ ._-]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const hasCitationMarkers = text => _.isString(text) && ANY_MARKER_TEST_REGEX.test(text);

export const normalizeCitationMarkers = text => {
  if (!hasCitationMarkers(text)) {
    return text;
  }

  return text
    .replace(
      THREE_PART_MARKER_REGEX,
      (_match, source, section) =>
        `[^cite:${slugifyCitationPart(source)}-${slugifyCitationPart(section)}]`,
    )
    .replace(KEY_OR_REF_MARKER_REGEX, (_match, key) => `[^cite:${key.toLowerCase()}]`);
};

// Splits text into ordered `{ type: 'text', value }` / `{ type: 'citation', key }`
// segments after normalising every marker shape. Empty text segments are dropped.
export const splitCitationSegments = text => {
  if (!_.isString(text) || text === '') {
    return [];
  }

  return normalizeCitationMarkers(text)
    .split(CLEAN_MARKER_SPLIT_REGEX)
    .filter(part => part !== '')
    .map(part => {
      const match = CLEAN_MARKER_KEY_REGEX.exec(part);

      return match ? { type: 'citation', key: match[1] } : { type: 'text', value: part };
    });
};

const getCitationKey = citation => citation?.citationId ?? citation?.id ?? null;

export const isResolvedCitation = citation =>
  !_.isNil(citation) &&
  !!(citation.sourceShortName || citation.sourceName || citation.sectionTitle || citation.title);

// Matches a marker key against a citations list by citation key (case-insensitive) or
// by numeric CitationNumber. A resolved record wins over an unresolved stub with the
// same key.
export const findCitationByKey = (citations, key) => {
  if (_.isEmpty(citations) || _.isNil(key)) {
    return null;
  }

  const normalizedKey = String(key).toLowerCase();
  const isNumeric = NUMERIC_KEY_REGEX.test(normalizedKey);
  const matches = citations.filter(
    citation =>
      String(getCitationKey(citation) ?? '').toLowerCase() === normalizedKey ||
      (isNumeric && String(citation?.citationNumber ?? '') === normalizedKey),
  );

  return matches.find(isResolvedCitation) ?? matches[0] ?? null;
};

// Concatenates citation lists, de-duplicating by key and keeping the first resolved
// record for each key.
export const mergeCitations = (...lists) =>
  lists
    .filter(Array.isArray)
    .flat()
    .reduce((merged, citation) => {
      const key = String(getCitationKey(citation) ?? '').toLowerCase();

      if (!key) {
        return merged;
      }

      const existingIndex = merged.findIndex(
        item => String(getCitationKey(item) ?? '').toLowerCase() === key,
      );

      if (existingIndex === -1) {
        return [...merged, citation];
      }

      if (!isResolvedCitation(merged[existingIndex]) && isResolvedCitation(citation)) {
        return merged.map((item, index) => (index === existingIndex ? citation : item));
      }

      return merged;
    }, []);

// The plain label shown for a citation: "FNMA B3-6-02" when resolved, otherwise a neutral
// label derived from the marker key itself — never the raw marker.
export const formatCitationLabel = (citation, key) => {
  if (isResolvedCitation(citation)) {
    const source = citation.sourceShortName || citation.sourceName || '';
    const section = citation.sectionNumber ?? '';

    return _.compact([source, section]).join(' ') || citation.sectionTitle || citation.title;
  }

  const rawKey = String(key ?? getCitationKey(citation) ?? '');

  return NUMERIC_KEY_REGEX.test(rawKey) ? `Guideline ref ${rawKey}` : rawKey.toUpperCase();
};

// Shapes an API citation (or an unresolved key) into the props the shared chat
// GuidelineExpanderCard / GuidelineCitationBadge expect.
export const buildCitationDisplayProps = (citation, key) => ({
  id: String(getCitationKey(citation) ?? key ?? ''),
  sourceId: citation?.sourceId || null,
  sectionId: citation?.sectionId ?? null,
  source: citation?.sourceShortName || citation?.sourceName || '',
  section: citation?.sectionNumber ?? '',
  title: citation?.sectionTitle ?? citation?.title ?? '',
  excerpt: citation?.excerpt ?? citation?.snippet ?? '',
});
